﻿(() => {
  const KIND_LABELS = {
    recipe: 'RECIPES',
    idea: 'IDEAS',
    technique: 'TECHNIQUES',
    ingredient: 'INGREDIENTS',
    tag: 'TOPICS',
  };

  const state = {
    loaded: false,
    loading: null,
    records: [],
    byKind: {},
    query: '',
    activeSuggestIndex: -1,
    suggestItems: [],
    logTimer: null,
  };

  function assetUrl() {
    var el = document.querySelector('[data-jkb-search-index]');
    if (el && el.getAttribute('data-jkb-search-index')) {
      return el.getAttribute('data-jkb-search-index');
    }
    var link = document.querySelector('link[rel="preload"][as="fetch"][href*="jkb-search-index"]');
    if (link && link.href) {
      return link.href;
    }
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return `${window.Shopify.routes.root}assets/jkb-search-index.json`;
    }
    return '/assets/jkb-search-index.json';
  }

  async function ensureLoaded() {
    if (state.loaded) return state.records;
    if (state.loading) return state.loading;
    state.loading = fetch(assetUrl(), { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`search index fetch failed: ${r.status}`);
        return r.json();
      })
      .then((rows) => {
        state.records = Array.isArray(rows) ? rows : [];
        state.byKind = groupByKind(state.records);
        state.loaded = true;
        return state.records;
      })
      .catch(() => {
        state.records = [];
        state.byKind = {};
        state.loaded = true;
        return state.records;
      });
    return state.loading;
  }

  function groupByKind(rows) {
    const out = {};
    rows.forEach((r) => {
      const k = String(r.k || '');
      if (!out[k]) out[k] = [];
      out[k].push(r);
    });
    return out;
  }

  function tokenize(text) {
    return String(text || '')
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  function levenshteinAtMostOne(a, b) {
    if (!a || !b) return false;
    if (Math.abs(a.length - b.length) > 1) return false;
    if (a === b) return true;
    let edits = 0;
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) {
        i += 1;
        j += 1;
        continue;
      }
      edits += 1;
      if (edits > 1) return false;
      if (a.length > b.length) i += 1;
      else if (a.length < b.length) j += 1;
      else {
        i += 1;
        j += 1;
      }
    }
    if (i < a.length || j < b.length) edits += 1;
    return edits <= 1;
  }

  function scoreRecord(q, rec) {
    if (!q) return 0;
    const query = q.toLowerCase();
    const title = String(rec.t || '').toLowerCase();
    const summary = String(rec.s || '').toLowerCase();
    const tags = String(rec.g || '').toLowerCase();
    const terms = String(rec.x || '').toLowerCase();

    if (title.startsWith(query)) return 100;
    if (title.includes(query)) return 80;
    if (terms.includes(query)) return 60;
    if (tags.includes(query)) return 40;
    if (summary.includes(query)) return 20;

    const titleTokens = tokenize(title);
    for (const token of titleTokens) {
      if (levenshteinAtMostOne(query, token)) return 10;
    }
    return 0;
  }

  function searchRecords(query, filters) {
    const q = String(query || '').trim().toLowerCase();
    let rows = state.records.slice();

    if (filters) {
      rows = rows.filter((r) => applyFilters(r, filters));
    }

    if (!q) return rows;

    return rows
      .map((r) => ({ r, score: scoreRecord(q, r) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.r);
  }

  function applyFilters(rec, filters) {
    if (!filters) return true;
    if (filters.kinds.size > 0 && !filters.kinds.has(rec.k)) return false;

    if (filters.time.size > 0) {
      const m = Number(rec.m || 0);
      const timeHit = Array.from(filters.time).some((bucket) => {
        if (bucket === '15') return m > 0 && m <= 15;
        if (bucket === '30') return m > 15 && m <= 30;
        if (bucket === '60') return m > 30 && m <= 60;
        if (bucket === '61') return m > 60;
        return false;
      });
      if (!timeHit) return false;
    }

    if (filters.difficulty.size > 0) {
      const d = Number(rec.d || 0);
      const diffHit = Array.from(filters.difficulty).some((label) => {
        if (label === 'easy') return d > 0 && d <= 2;
        if (label === 'level-up') return d === 3;
        if (label === 'challenge') return d >= 4;
        return false;
      });
      if (!diffHit) return false;
    }

    const tags = String(rec.g || '').split(',').map((t) => t.trim()).filter(Boolean);
    if (filters.access.size > 0 && !tags.some((t) => filters.access.has(t))) return false;
    if (filters.dietary.size > 0 && !tags.some((t) => filters.dietary.has(t))) return false;
    return true;
  }

  function renderSuggest(container, items) {
    if (!container) return;
    if (!items.length) {
      container.hidden = true;
      container.innerHTML = '';
      state.suggestItems = [];
      state.activeSuggestIndex = -1;
      return;
    }

    const grouped = {};
    items.forEach((it) => {
      if (!grouped[it.k]) grouped[it.k] = [];
      if (grouped[it.k].length < 3) grouped[it.k].push(it);
    });

    const ordered = ['idea', 'technique', 'ingredient', 'recipe', 'tag'];
    const flat = [];
    const html = ordered
      .filter((k) => grouped[k] && grouped[k].length > 0)
      .map((k) => {
        const rows = grouped[k]
          .map((r) => {
            flat.push(r);
            return `<button type=\"button\" class=\"jkb-suggest-item\" data-url=\"${escapeAttr(r.u)}\">${escapeHtml(r.t)}</button>`;
          })
          .join('');
        return `<div class=\"jkb-suggest-group\"><p class=\"jkb-suggest-kind\">${KIND_LABELS[k]}</p>${rows}</div>`;
      })
      .join('');

    state.suggestItems = flat;
    state.activeSuggestIndex = -1;
    container.innerHTML = html;
    container.hidden = false;

    container.querySelectorAll('.jkb-suggest-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-url');
        if (url) window.location.href = url;
      });
    });
  }

  function nextSuggest(step, container) {
    if (!container || !state.suggestItems.length) return;
    const buttons = Array.from(container.querySelectorAll('.jkb-suggest-item'));
    if (!buttons.length) return;
    state.activeSuggestIndex += step;
    if (state.activeSuggestIndex < 0) state.activeSuggestIndex = buttons.length - 1;
    if (state.activeSuggestIndex >= buttons.length) state.activeSuggestIndex = 0;
    buttons.forEach((b, idx) => {
      b.classList.toggle('is-active', idx === state.activeSuggestIndex);
    });
  }

  function enterSuggest(container) {
    if (!container || state.activeSuggestIndex < 0) return false;
    const buttons = Array.from(container.querySelectorAll('.jkb-suggest-item'));
    const active = buttons[state.activeSuggestIndex];
    if (active) active.click();
    return !!active;
  }

  function buildFiltersFromUrl() {
    const p = new URLSearchParams(window.location.search);
    return {
      query: p.get('q') || '',
      kinds: new Set((p.get('kind') || '').split(',').filter(Boolean)),
      time: new Set((p.get('time') || '').split(',').filter(Boolean)),
      difficulty: new Set((p.get('difficulty') || '').split(',').filter(Boolean)),
      access: new Set((p.get('access') || '').split(',').filter(Boolean)),
      dietary: new Set((p.get('dietary') || '').split(',').filter(Boolean)),
    };
  }

  function writeFiltersToUrl(filters) {
    const p = new URLSearchParams();
    if (filters.query) p.set('q', filters.query);
    if (filters.kinds.size) p.set('kind', Array.from(filters.kinds).join(','));
    if (filters.time.size) p.set('time', Array.from(filters.time).join(','));
    if (filters.difficulty.size) p.set('difficulty', Array.from(filters.difficulty).join(','));
    if (filters.access.size) p.set('access', Array.from(filters.access).join(','));
    if (filters.dietary.size) p.set('dietary', Array.from(filters.dietary).join(','));
    const next = `${window.location.pathname}${p.toString() ? `?${p}` : ''}`;
    window.history.replaceState({}, '', next);
  }

  function renderCheckboxGroup(host, title, entries, setRef, onChange, enabledSet) {
    if (!host) return;
    host.innerHTML = '';
    if (!entries.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'jkb-filter-group';
    const h = document.createElement('h3');
    h.textContent = title;
    wrap.appendChild(h);
    entries.forEach((e) => {
      const id = `${title}-${e.value}`.replace(/[^a-z0-9_-]/gi, '-');
      const label = document.createElement('label');
      label.className = 'jkb-filter-option';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = e.value;
      input.checked = setRef.has(e.value);
      const enabled = !enabledSet || enabledSet.has(e.value);
      input.disabled = !enabled;
      label.classList.toggle('is-disabled', !enabled);
      input.addEventListener('change', () => {
        if (input.checked) setRef.add(e.value);
        else setRef.delete(e.value);
        onChange();
      });
      const text = document.createElement('span');
      text.textContent = e.label;
      label.appendChild(input);
      label.appendChild(text);
      wrap.appendChild(label);
    });
    host.appendChild(wrap);
  }

  function filterAvailability(filters, axis) {
    const test = {
      query: filters.query,
      kinds: new Set(filters.kinds),
      time: new Set(filters.time),
      difficulty: new Set(filters.difficulty),
      access: new Set(filters.access),
      dietary: new Set(filters.dietary),
    };
    test[axis] = new Set();
    const base = searchRecords(filters.query, test);
    const out = new Set();
    base.forEach((r) => {
      if (axis === 'kinds') out.add(r.k);
      if (axis === 'time') {
        const m = Number(r.m || 0);
        if (m > 0 && m <= 15) out.add('15');
        else if (m <= 30) out.add('30');
        else if (m <= 60) out.add('60');
        else out.add('61');
      }
      if (axis === 'difficulty') {
        const d = Number(r.d || 0);
        if (d > 0 && d <= 2) out.add('easy');
        else if (d === 3) out.add('level-up');
        else if (d >= 4) out.add('challenge');
      }
      if (axis === 'access' || axis === 'dietary') {
        String(r.g || '').split(',').forEach((tag) => {
          const t = tag.trim();
          if (!t) return;
          if (axis === 'access' && (t === 'local-supermarket' || t === 'asian-supermarket')) out.add(t);
          if (axis === 'dietary' && (t === 'vegan' || t === 'gluten-free')) out.add(t);
        });
      }
    });
    return out;
  }

  function renderMainSearch(root) {
    const input = root.querySelector('[data-jkb-search-input]');
    const suggest = root.querySelector('[data-jkb-search-suggest]');
    const empty = root.querySelector('[data-jkb-search-empty]');
    const filtersBox = root.querySelector('[data-jkb-search-filters]');
    const summary = root.querySelector('[data-jkb-search-result-summary]');
    const results = root.querySelector('[data-jkb-search-results]');
    const cta = root.querySelector('[data-jkb-search-cta]');

    const filters = buildFiltersFromUrl();
    input.value = filters.query;

    function rerender() {
      filters.query = String(input.value || '').trim();
      writeFiltersToUrl(filters);

      const rows = searchRecords(filters.query, filters);
      const showResults = filters.query.length > 0;
      empty.hidden = showResults;
      filtersBox.hidden = !showResults;
      summary.hidden = !showResults;

      if (showResults) {
        summary.textContent = `${rows.length} results`;
        renderResults(results, rows);
        renderFilterUi(root, filters, rerender);
        maybeLogSearch(root, filters.query, rows.length, filters);
      } else {
        renderEmpty(root);
        results.innerHTML = '';
      }

      const label = root.getAttribute('data-jkb-search-cta-label') || '';
      const url = root.getAttribute('data-jkb-search-cta-url') || '';
      if (showResults && label && url) {
        cta.innerHTML = `<a href=\"${escapeAttr(url)}\" class=\"button\">${escapeHtml(label)}</a>`;
        cta.hidden = false;
      } else {
        cta.hidden = true;
      }
    }

    input.addEventListener('focus', async () => {
      await ensureLoaded();
      if (input.value.trim().length >= 2) {
        const list = searchRecords(input.value, null).slice(0, 10);
        renderSuggest(suggest, list);
      }
    });

    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (q.length >= 2) {
        const list = searchRecords(q, null).slice(0, 10);
        renderSuggest(suggest, list);
      } else {
        renderSuggest(suggest, []);
      }
      rerender();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextSuggest(1, suggest);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextSuggest(-1, suggest);
      } else if (e.key === 'Enter') {
        if (enterSuggest(suggest)) e.preventDefault();
      } else if (e.key === 'Escape') {
        renderSuggest(suggest, []);
      }
    });

    document.addEventListener('click', (ev) => {
      if (!root.contains(ev.target)) renderSuggest(suggest, []);
    });

    rerender();
  }

  function renderFilterUi(root, filters, rerender) {
    const kindHost = root.querySelector('[data-jkb-kind-filters]');
    const timeHost = root.querySelector('[data-jkb-time-filters]');
    const diffHost = root.querySelector('[data-jkb-difficulty-filters]');
    const accessHost = root.querySelector('[data-jkb-access-filters]');
    const dietaryHost = root.querySelector('[data-jkb-dietary-filters]');

    renderCheckboxGroup(
      kindHost,
      'Kind',
      [
        { value: 'recipe', label: 'Recipes' },
        { value: 'idea', label: 'Ideas' },
        { value: 'technique', label: 'Techniques' },
        { value: 'ingredient', label: 'Ingredients' },
      ],
      filters.kinds,
      rerender,
      filterAvailability(filters, 'kinds')
    );

    renderCheckboxGroup(
      timeHost,
      'Time',
      [
        { value: '15', label: 'up to 15 min' },
        { value: '30', label: 'up to 30 min' },
        { value: '60', label: 'up to 1 hour' },
        { value: '61', label: 'over 1 hour' },
      ],
      filters.time,
      rerender,
      filterAvailability(filters, 'time')
    );

    renderCheckboxGroup(
      diffHost,
      'Difficulty',
      [
        { value: 'easy', label: 'Easy' },
        { value: 'level-up', label: 'Level Up' },
        { value: 'challenge', label: 'Challenge' },
      ],
      filters.difficulty,
      rerender,
      filterAvailability(filters, 'difficulty')
    );

    renderCheckboxGroup(
      accessHost,
      'Ingredients',
      [
        { value: 'local-supermarket', label: 'local supermarket' },
        { value: 'asian-supermarket', label: 'asian supermarket' },
      ],
      filters.access,
      rerender,
      filterAvailability(filters, 'access')
    );

    renderCheckboxGroup(
      dietaryHost,
      'Diet',
      [
        { value: 'vegan', label: 'Vegan' },
        { value: 'gluten-free', label: 'Gluten-Free' },
      ],
      filters.dietary,
      rerender,
      filterAvailability(filters, 'dietary')
    );
  }

  function renderResults(host, rows) {
    host.innerHTML = '';
    if (!rows.length) {
      host.innerHTML = '<p>No results. Try another word.</p>';
      return;
    }
    const html = rows
      .map(
        (r) => `<article class=\"jkb-result-card\"><a href=\"${escapeAttr(r.u)}\"><h3>${escapeHtml(r.t)}</h3><p>${escapeHtml(r.s || '')}</p><small>${escapeHtml(KIND_LABELS[r.k] || r.k)}</small></a></article>`
      )
      .join('');
    host.innerHTML = html;
  }

  function renderEmpty(root) {
    const popularHost = root.querySelector('[data-jkb-popular-list]');
    const typeHost = root.querySelector('[data-jkb-type-counts]');
    const topicHost = root.querySelector('[data-jkb-topic-list]');

    const recipes = state.records
      .filter((r) => r.k === 'recipe')
      .slice()
      .sort((a, b) => {
        const pa = Number(a.p || 0);
        const pb = Number(b.p || 0);
        if (pa > 0 && pb > 0) return pa - pb;
        if (pa > 0) return -1;
        if (pb > 0) return 1;
        return 0;
      })
      .slice(0, 4);

    popularHost.innerHTML = recipes.map((r) => `<li><a href=\"${escapeAttr(r.u)}\">${escapeHtml(r.t)}</a></li>`).join('');

    const counts = ['recipe', 'idea', 'technique', 'ingredient']
      .map((k) => ({ k, count: state.records.filter((r) => r.k === k).length }))
      .map((x) => `${KIND_LABELS[x.k]} ${x.count}`)
      .join(' · ');
    typeHost.textContent = counts;

    const topics = state.records
      .filter((r) => r.k === 'tag')
      .slice(0, 12)
      .map((r) => `<a href=\"${escapeAttr(r.u)}\">${escapeHtml(r.t)}</a>`)
      .join(' · ');
    topicHost.innerHTML = topics;
  }

  function maybeLogSearch(root, query, resultCount, filters) {
    if (!query || resultCount >= 5) return;
    const endpoint = root.getAttribute('data-jkb-log-endpoint') || window.JKB_SEARCH_LOG_ENDPOINT || '';
    const token = root.getAttribute('data-jkb-log-token') || window.JKB_WEB_API_TOKEN || '';
    if (!endpoint || !token) return;

    clearTimeout(state.logTimer);
    state.logTimer = setTimeout(() => {
      const payload = {
        action: 'search_log',
        token,
        query,
        result_count: resultCount,
        filters: [
          filters.kinds.size ? `kind:${Array.from(filters.kinds).join(',')}` : '',
          filters.time.size ? `time:${Array.from(filters.time).join(',')}` : '',
          filters.difficulty.size ? `difficulty:${Array.from(filters.difficulty).join(',')}` : '',
        ].filter(Boolean).join('|'),
        kind: filters.kinds.size ? Array.from(filters.kinds).join(',') : '',
      };

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }, 2000);
  }

  function setupHeaderSearch(root) {
    const input = root.querySelector('[data-jkb-search-input]');
    const suggest = root.querySelector('[data-jkb-search-suggest]');
    if (!input || !suggest) return;

    input.addEventListener('focus', async () => {
      await ensureLoaded();
      const q = input.value.trim();
      if (q.length >= 2) renderSuggest(suggest, searchRecords(q, null).slice(0, 10));
    });

    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (q.length < 2) return renderSuggest(suggest, []);
      renderSuggest(suggest, searchRecords(q, null).slice(0, 10));
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextSuggest(1, suggest);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextSuggest(-1, suggest);
      } else if (e.key === 'Enter') {
        if (enterSuggest(suggest)) e.preventDefault();
      } else if (e.key === 'Escape') {
        renderSuggest(suggest, []);
      }
    });
  }

  function escapeHtml(text) {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(text) {
    return escapeHtml(text).replace(/`/g, '');
  }

  async function init() {
    await ensureLoaded();
    document.querySelectorAll('[data-jkb-search-root]').forEach((root) => {
      if (root.getAttribute('data-jkb-search-page') === 'main') {
        renderMainSearch(root);
      } else {
        setupHeaderSearch(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

